// src/lib/supabase.js
import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY
const dbProvider = process.env.REACT_APP_DB_PROVIDER || 'supabase'

// Check if Supabase is properly configured with valid URLs
const isValidUrl = (url) => {
  try {
    return url && url.startsWith('https://') && url.includes('.supabase.co')
  } catch {
    return false
  }
}

const isSupabaseConfigured = isValidUrl(supabaseUrl) &&
  supabaseAnonKey &&
  supabaseAnonKey.length > 50 // JWT tokens are long

console.log('🔐 Supabase Config Check:', {
  hasUrl: !!supabaseUrl,
  urlValid: isValidUrl(supabaseUrl),
  hasKey: !!supabaseAnonKey,
  keyLength: supabaseAnonKey?.length,
  isConfigured: isSupabaseConfigured,
  dbProvider
})

if (!isSupabaseConfigured && dbProvider === 'supabase') {
  throw new Error('Missing Supabase environment variables. Required when REACT_APP_DB_PROVIDER=supabase')
}

// When using local DB, auth is handled via localStorage (no Supabase needed)
if (dbProvider === 'local') {
  console.log('💾 Local DB mode: Supabase auth is bypassed, using localStorage auth')
}

// Always create the real Supabase client if we have valid config
// This allows using Supabase for auth even when using Google Sheets for data
export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true
      }
    })
  : {
      // Dummy client for when Supabase is not configured at all
      auth: {
        getUser: async () => ({ data: { user: { id: 'sheets-user', email: 'sheets@local' } } }),
        getSession: async () => ({ data: { session: null } }),
        signInWithOAuth: async () => { console.warn('Supabase auth not configured'); return { data: null, error: new Error('Supabase not configured') } },
        signOut: async () => ({ error: null }),
        onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } })
      },
      from: () => ({
        select: () => ({ eq: () => ({ single: async () => ({ data: null, error: new Error('Supabase not configured') }) }) }),
        insert: () => ({ select: () => ({ single: async () => ({ data: null, error: new Error('Supabase not configured') }) }) }),
        update: () => ({ eq: () => ({ select: () => ({ single: async () => ({ data: null, error: new Error('Supabase not configured') }) }) }) }),
        delete: () => ({ eq: async () => ({ error: new Error('Supabase not configured') }) }),
        upsert: () => ({ select: () => ({ single: async () => ({ data: null, error: new Error('Supabase not configured') }) }) })
      })
    }

// Flag to check if Supabase is fully configured
export const isSupabaseEnabled = isSupabaseConfigured

// Auth helpers
export const signInWithGoogle = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${window.location.origin}/`,
      queryParams: {
        access_type: 'offline',
        prompt: 'consent',
      }
    }
  })
  return { data, error }
}

export const signOut = async () => {
  const { error } = await supabase.auth.signOut()
  return { error }
}

export const getCurrentUser = () => {
  return supabase.auth.getUser()
}

export const getSession = () => {
  return supabase.auth.getSession()
}

// Database helpers
export const getUserProfile = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single()

  return { data, error }
}

export const createUserProfile = async (user) => {
  const { data, error } = await supabase
    .from('profiles')
    .insert([
      {
        id: user.id,
        email: user.email,
        full_name: user.user_metadata?.full_name || user.email?.split('@')[0],
        avatar_url: user.user_metadata?.avatar_url,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    ])
    .select()
    .single()

  return { data, error }
}

export const updateUserProfile = async (userId, updates) => {
  const { data, error } = await supabase
    .from('profiles')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', userId)
    .select()
    .single()

  return { data, error }
}